import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';
import { getErrorMessage } from './errorUtils';

// ── Compression presets ───────────────────────────────────────────────────────
// Pass one of these as the `options` argument to uploadToFirebaseStorage to
// override the default quality / size limits per image type.

/** Store logo, QR codes, client logos — small and fast */
export const PRESET_LOGO = {
    maxSizeBytes: 500 * 1024,   // 500 KB
    maxDimension: 1200,
    quality: 0.85,
};

/** Product images, banners, collabs, collection covers */
export const PRESET_PRODUCT = {
    maxSizeBytes: 2 * 1024 * 1024,  // 2 MB
    maxDimension: 2000,
    quality: 0.88,
};

/** Customer design artwork / print-ready files */
export const PRESET_DESIGN = {
    maxSizeBytes: 8 * 1024 * 1024,  // 8 MB
    maxDimension: 3000,
    quality: 0.85,
};

const DEFAULT_PRESET = PRESET_PRODUCT;

/**
 * Compress a File/Blob using an offscreen Canvas if it exceeds the preset limits.
 * - Scales down to maxDimension while preserving aspect ratio.
 * - Converts to JPEG (best compression). PNG transparency → white background.
 * - SVG and GIF are skipped (can't be canvas-compressed meaningfully).
 * - Returns the original file unchanged if already within the size limit.
 *
 * @param {File|Blob} file
 * @param {{ maxSizeBytes: number, maxDimension: number, quality: number }} preset
 * @returns {Promise<File|Blob>}
 */
async function compressIfNeeded(file, preset = DEFAULT_PRESET) {
    const { maxSizeBytes, maxDimension, quality } = preset;
    if (file.size <= maxSizeBytes) return file;

    const type = file.type || '';
    if (type === 'image/svg+xml' || type === 'image/gif') {
        console.warn(`firebaseStorage: skipping compression for ${type}`);
        return file;
    }

    return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { width, height } = img;
            if (width > maxDimension || height > maxDimension) {
                const ratio = Math.min(maxDimension / width, maxDimension / height);
                width  = Math.round(width  * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width  = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) { resolve(file); return; }

                    const compressed = new File(
                        [blob],
                        file.name?.replace(/\.[^.]+$/, '.jpg') || 'upload.jpg',
                        { type: 'image/jpeg', lastModified: Date.now() }
                    );
                    console.log(
                        `firebaseStorage: ${(file.size / 1024 / 1024).toFixed(2)} MB → ` +
                        `${(compressed.size / 1024 / 1024).toFixed(2)} MB`
                    );
                    resolve(compressed);
                },
                'image/jpeg',
                quality,
            );
        };

        img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
        img.src = objectUrl;
    });
}

/**
 * Uploads a file to Firebase Storage, compressing it first if it exceeds the
 * preset size limits.
 *
 * @param {File} file - The file to upload.
 * @param {string} folder - The folder path in storage (e.g., 'products', 'design-images').
 * @param {{ maxSizeBytes?: number, maxDimension?: number, quality?: number }} [options]
 *   Override any preset value. Defaults to PRESET_PRODUCT.
 * @returns {Promise<{url: string|null, error: string|null}>}
 */
export async function uploadToFirebaseStorage(file, folder = 'general', options = {}) {
    if (!file) return { url: null, error: 'No file provided.' };
    if (!storage) return { url: null, error: 'Firebase Storage is not initialized.' };

    try {
        const preset = { ...DEFAULT_PRESET, ...options };
        const fileToUpload = (file instanceof File || file instanceof Blob)
            ? await compressIfNeeded(file, preset)
            : file;

        // Create a unique filename to prevent overwrites
        const timestamp = Date.now();
        const safeName = (file.name || 'upload').replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const fileName = `${timestamp}_${safeName}`;

        const cleanFolder = folder.replace(/^\/+|\/+$/g, '');
        const fullPath = cleanFolder ? `${cleanFolder}/${fileName}` : fileName;

        const storageRef = ref(storage, fullPath);
        const snapshot = await uploadBytes(storageRef, fileToUpload);
        const downloadUrl = await getDownloadURL(snapshot.ref);

        return { url: downloadUrl, error: null };
    } catch (error) {
        console.error('Firebase Storage upload error:', error);
        return { url: null, error: getErrorMessage(error, 'Failed to upload to Firebase Storage.') };
    }
}

/**
 * Uploads all design images to Firebase Storage in PARALLEL.
 *
 * @param {Object} designs - { [locationName]: File|Blob|string|null }
 * @returns {Promise<{ urls: Object, error: string|null }>}
 */
export async function uploadDesignImagesToFirebase(designs) {
    try {
        const entries = Object.entries(designs || {});

        const settled = await Promise.all(
            entries.map(async ([location, file]) => {
                if (!file)                                   return [location, null];
                if (typeof file === 'string')                return [location, file];
                if (file instanceof File || file instanceof Blob) {
                    const { url, error } = await uploadToFirebaseStorage(file, 'jg-printing/designs', PRESET_DESIGN);
                    if (error) throw new Error(`Failed to upload "${location}" design: ${error}`);
                    return [location, url];
                }
                return [location, null];
            })
        );

        return { urls: Object.fromEntries(settled), error: null };
    } catch (err) {
        console.error('uploadDesignImagesToFirebase error:', err);
        return { urls: null, error: getErrorMessage(err, 'Design upload failed.') };
    }
}

/**
 * Deletes a file from Firebase Storage by its download URL.
 *
 * @param {string} url - Firebase Storage download URL
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function deleteFromFirebaseStorage(url) {
    if (!url || typeof url !== 'string') return { success: true, error: null };
    if (!storage) return { success: false, error: 'Firebase Storage is not initialized.' };

    try {
        const urlObj = new URL(url);
        const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
        if (!pathMatch) return { success: false, error: 'Invalid Firebase Storage URL.' };

        const filePath = decodeURIComponent(pathMatch[1]);
        await deleteObject(ref(storage, filePath));
        return { success: true, error: null };
    } catch (error) {
        if (error.code === 'storage/object-not-found') return { success: true, error: null };
        return { success: false, error: getErrorMessage(error, 'Failed to delete file from storage.') };
    }
}

